import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, User, CreditCard, Award, ArrowLeft, Edit2, Check, X, Gift, Share2, Copy, ExternalLink, Key, Eye, EyeOff, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PROFESSIONS = [
  'Content Creator',
  'Marketer',
  'Social Media Manager',
  'Copywriter',
  'Designer',
  'Sales Team',
  'Founder/Entrepreneur',
  'Educator',
  'Product Manager',
  'Business Analyst',
  'Other'
];

const INTERESTS = [
  'Social Media Marketing',
  'Content Strategy',
  'Graphic Design',
  'Video Production',
  'Email Marketing',
  'SEO/SEM',
  'Brand Strategy',
  'Data Analytics',
  'E-commerce',
  'Public Relations',
  'Advertising',
  'Market Research'
];

// API Key Manager Component
const APIKeyManager = () => {
  const [apiKey, setApiKey] = useState(null);
  const [hasKey, setHasKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    fetchAPIKey();
  }, []);

  const fetchAPIKey = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/api-key`, {
        withCredentials: true
      });
      
      if (response.data.has_api_key) {
        setHasKey(true);
        setApiKey(response.data.api_key_masked);
      } else {
        setHasKey(false);
      }
    } catch (error) {
      console.error('Error fetching API key:', error);
    }
  };

  const generateAPIKey = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/user/generate-api-key`, {}, {
        withCredentials: true
      });
      
      if (response.data.success) {
        setApiKey(response.data.api_key);
        setHasKey(true);
        setShowKey(true);
        toast.success('API key generated successfully! Copy it now - you won\'t see it again.');
      }
    } catch (error) {
      console.error('Error generating API key:', error);
      toast.error('Failed to generate API key. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const revokeAPIKey = async () => {
    if (!confirm('Are you sure? This will invalidate your current API key and break any integrations using it.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.delete(`${API_URL}/api/user/api-key`, {
        withCredentials: true
      });
      
      if (response.data.success) {
        setApiKey(null);
        setHasKey(false);
        setShowKey(false);
        toast.success('API key revoked successfully');
      }
    } catch (error) {
      console.error('Error revoking API key:', error);
      toast.error('Failed to revoke API key');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (apiKey) {
      navigator.clipboard.writeText(apiKey);
      toast.success('API key copied to clipboard!');
    }
  };

  return (
    <div className="space-y-4">
      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2 flex items-center gap-2">
          <Key className="w-4 h-4" />
          Use emoQ.ai in ChatGPT
        </h4>
        <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
          Generate an API key to use emoQ.ai as a custom GPT in ChatGPT. Analyze content directly in your conversations!
        </p>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.open('https://chatgpt.com/gpts/discovery', '_blank')}
            className="text-xs"
          >
            <ExternalLink className="w-3 h-3 mr-1" />
            Find in GPT Store
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => toast.info('Setup guide available in documentation')}
            className="text-xs"
          >
            📖 Setup Guide
          </Button>
        </div>
      </div>

      {/* API Key Display/Generation */}
      {!hasKey ? (
        <div className="text-center py-6">
          <Key className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            No API key generated yet. Create one to start using emoQ.ai in ChatGPT.
          </p>
          <Button 
            onClick={generateAPIKey}
            disabled={loading}
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Key className="w-4 h-4 mr-2" />
                Generate API Key
              </>
            )}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {/* API Key Display */}
          <div>
            <Label htmlFor="api-key" className="text-sm font-medium mb-2 block">
              Your API Key
            </Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  id="api-key"
                  type={showKey ? "text" : "password"}
                  value={apiKey || ""}
                  readOnly
                  className="font-mono text-sm pr-10"
                />
                <button
                  onClick={() => setShowKey(!showKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={copyToClipboard}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            {!showKey && (
              <p className="text-xs text-gray-500 mt-1">
                Key is masked for security. Click the eye icon to reveal.
              </p>
            )}
          </div>

          {/* Warning */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-3">
            <p className="text-xs text-amber-900 dark:text-amber-200">
              ⚠️ <strong>Keep your API key secret!</strong> Don't share it publicly. Anyone with your key can use your credits.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={generateAPIKey}
              disabled={loading}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Regenerate
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={revokeAPIKey}
              disabled={loading}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Revoke
            </Button>
          </div>

          {/* Usage Instructions */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded p-3 text-xs space-y-2">
            <p className="font-semibold">How to use in ChatGPT:</p>
            <ol className="list-decimal list-inside space-y-1 text-gray-700 dark:text-gray-300">
              <li>Find "emoQ.ai" in the GPT Store</li>
              <li>Start a conversation with the GPT</li>
              <li>When prompted, paste your API key</li>
              <li>Start analyzing content!</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
};

const Profile = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, updateCredits } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    profession: '',
    interests: [],
    gender: '',
    age: ''
  });

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);

  // Referral state
  const [referralStats, setReferralStats] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        profession: user.profession || '',
        interests: user.interests || [],
        gender: user.gender || '',
        age: user.age || ''
      });
    }
  }, [user]);

  const handleInterestToggle = (interest) => {
    setProfileData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('authToken');
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      await axios.put(
        `${API_URL}/api/user/profile`,
        profileData,
        { 
          headers,
          withCredentials: true 
        }
      );
      toast.success('Profile updated successfully!');
      setEditing(false);
      // Refresh user data
      await updateCredits();
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  // Fetch referral stats
  const fetchReferralStats = async () => {
    setReferralLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_URL}/api/referral-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching referral stats:', error);
    } finally {
      setReferralLoading(false);
    }
  };

  // Redeem promo code
  const handleRedeemCode = async () => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }

    setPromoLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${API_URL}/api/redeem-promo-code`,
        { code: promoCode.toUpperCase() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setPromoCode('');
      // Refresh user credits
      await updateCredits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to redeem code');
    } finally {
      setPromoLoading(false);
    }
  };

  // Copy referral link
  const copyReferralLink = () => {
    const link = `${window.location.origin}?ref=${referralStats.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  // Share via WhatsApp
  const shareWhatsApp = () => {
    const link = `${window.location.origin}?ref=${referralStats.referral_code}`;
    const message = `Check out emoQ.ai - the best emotional intelligence platform! Sign up with my link and we both benefit: ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  // Load referral stats on mount
  useEffect(() => {
    if (user) {
      fetchReferralStats();
    }
  }, [user]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/')} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">My Profile</h1>
          <div className="w-24"></div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Account Overview Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <CardTitle>Account Overview</CardTitle>
                  <CardDescription>{user?.email}</CardDescription>
                </div>
              </div>
              {user?.role === 'admin' && (
                <Badge className="bg-purple-600">Admin</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Available Credits</span>
                </div>
                <p className="text-3xl font-bold text-blue-600">{user?.credits || 0}</p>
                <Button 
                  size="sm" 
                  className="mt-3 w-full"
                  onClick={() => navigate('/pricing')}
                >
                  Top Up Credits
                </Button>
              </div>
              
              <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 mb-2">
                  <Award className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Member Status</span>
                </div>
                <p className="text-3xl font-bold text-green-600">Free</p>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="mt-3 w-full"
                  onClick={() => navigate('/pricing')}
                >
                  Upgrade to Pro
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Information Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Profile Information</CardTitle>
              {!editing ? (
                <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSave} disabled={loading}>
                    {loading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Save
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Name */}
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={profileData.name}
                onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                disabled={!editing}
                className="mt-1"
              />
            </div>

            {/* Email (Read-only) */}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={profileData.email}
                disabled
                className="mt-1 bg-gray-50 dark:bg-gray-800"
              />
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
            </div>

            {/* Profession */}
            <div>
              <Label htmlFor="profession">Field of Work / Profession</Label>
              <Select
                value={profileData.profession}
                onValueChange={(value) => setProfileData({...profileData, profession: value})}
                disabled={!editing}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select your profession" />
                </SelectTrigger>
                <SelectContent>
                  {PROFESSIONS.map((prof) => (
                    <SelectItem key={prof} value={prof}>
                      {prof}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Gender */}
            <div>
              <Label htmlFor="gender">Gender</Label>
              <Select
                value={profileData.gender}
                onValueChange={(value) => setProfileData({...profileData, gender: value})}
                disabled={!editing}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                  <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Age */}
            <div>
              <Label htmlFor="age">Age</Label>
              <Input
                id="age"
                type="number"
                min="13"
                max="120"
                value={profileData.age}
                onChange={(e) => setProfileData({...profileData, age: e.target.value})}
                disabled={!editing}
                className="mt-1"
                placeholder="Enter your age"
              />
            </div>

            {/* Interests */}
            <div>
              <Label>Interests (Select all that apply)</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {INTERESTS.map((interest) => (
                  <Badge
                    key={interest}
                    variant={profileData.interests.includes(interest) ? 'default' : 'outline'}
                    className={`cursor-pointer transition-all ${
                      editing ? 'hover:scale-105' : 'cursor-default'
                    } ${
                      profileData.interests.includes(interest)
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : ''
                    }`}
                    onClick={() => editing && handleInterestToggle(interest)}
                  >
                    {interest}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Redeem Promo Code Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              <Gift className="w-5 h-5 text-purple-600" />
              <CardTitle>Redeem Promo Code</CardTitle>
            </div>
            <CardDescription>Enter a promo code to add credits to your account</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex space-x-2">
                <Input
                  placeholder="Enter promo code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  className="flex-1 font-mono"
                  maxLength={10}
                />
                <Button
                  onClick={handleRedeemCode}
                  disabled={promoLoading || !promoCode.trim()}
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                >
                  {promoLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Redeem'
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                🎁 Promo codes are single-use only and add credits instantly
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Referral Program Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              <Share2 className="w-5 h-5 text-green-600" />
              <CardTitle>Referral Program</CardTitle>
            </div>
            <CardDescription>Earn 5 credits for every friend who joins!</CardDescription>
          </CardHeader>
          <CardContent>
            {referralLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              </div>
            ) : referralStats ? (
              <div className="space-y-4">
                {/* Referral Code */}
                <div>
                  <Label className="text-sm font-medium">Your Referral Code</Label>
                  <div className="mt-2 flex items-center space-x-2 p-3 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg border-2 border-green-200 dark:border-green-700">
                    <code className="flex-1 text-xl font-bold text-green-700 dark:text-green-400">
                      {referralStats.referral_code}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={copyReferralLink}
                      className="border-green-600 text-green-600 hover:bg-green-50"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                    <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                      {referralStats.total_referrals}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Referrals</div>
                  </div>
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                    <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                      {referralStats.total_credits_earned}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Credits Earned</div>
                  </div>
                </div>

                {/* Share Buttons */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Share Your Link</Label>
                  <div className="flex space-x-2">
                    <Button
                      onClick={shareWhatsApp}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                    <Button
                      onClick={copyReferralLink}
                      variant="outline"
                      className="flex-1"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Link
                    </Button>
                  </div>
                </div>

                {/* Referred Users List */}
                {referralStats.referred_users && referralStats.referred_users.length > 0 && (
                  <div className="mt-4">
                    <Label className="text-sm font-medium">Your Referrals</Label>
                    <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                      {referralStats.referred_users.map((ref, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded"
                        >
                          <div className="flex-1">
                            <div className="text-sm font-medium">{ref.email}</div>
                            <div className="text-xs text-gray-500">
                              {new Date(ref.registered_at).toLocaleDateString()}
                            </div>
                          </div>
                          <Badge variant="success" className="bg-green-600">
                            +{ref.credits_earned}⚡
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Info */}
                <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    💡 <strong>How it works:</strong> Share your referral code or link with friends. 
                    When they sign up, you'll automatically receive 5 credits!
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-500">Unable to load referral stats</p>
            )}
          </CardContent>
        </Card>

        {/* API Key for ChatGPT Integration Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              🤖 ChatGPT Integration
            </CardTitle>
            <CardDescription>Use emoQ.ai in your Custom ChatGPT</CardDescription>
          </CardHeader>
          <CardContent>
            <APIKeyManager />
          </CardContent>
        </Card>

        {/* Usage Statistics Card */}
        <Card>
          <CardHeader>
            <CardTitle>Usage Statistics</CardTitle>
            <CardDescription>Your account activity summary</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <span className="text-sm text-gray-700 dark:text-gray-300">Total Analyses</span>
                <span className="font-bold text-gray-900 dark:text-white">Coming Soon</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <span className="text-sm text-gray-700 dark:text-gray-300">Credits Used</span>
                <span className="font-bold text-gray-900 dark:text-white">Coming Soon</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <span className="text-sm text-gray-700 dark:text-gray-300">Member Since</span>
                <span className="font-bold text-gray-900 dark:text-white">{new Date(user?.created_at || Date.now()).toLocaleDateString()}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Profile;
